/**
 * DebtLedgerSubTab.jsx — Muhasebe FAZ 3.6/FAZ 6/FAZ 6.8, "Borç & Tahsilat" alt sekmesi.
 *
 * `useStudentLedger` ile borç/tahsilat read-model'ini çeker, üstte canonical
 * `summary` (toplam_borc/toplam_tahsilat/kalan_bakiye) kartlarını, altta genel
 * aksiyon barı (Onay Bekliyor rozeti · Döküm PDF İndir · İhtar PDF İndir ·
 * Tahsilat), `DebtLedgerTable`, `CollectionForm` Sheet'ini ve
 * `ReversePaymentDialog`'u gösterir.
 *
 * "Ödemeyi Geri Al" (FAZ 6.8): satır-bazlı tetikleyici `columnDefs.js`'in
 * `action` kolonunda (`reversePaymentActionCell`) — Fatura PDF ile AYNI
 * desen. Bu dosya yalnızca dialog'un tek instance'ını (`reverseRow` state)
 * ve "Onay Bekliyor" bekleyen-talepler rozet+popover'ını taşır. Para BURADA
 * hareket ETMEZ — `POST .../reverse` yalnızca maker-checker talebi açar,
 * gerçek hareket popover'daki "Onayla" aksiyonunda (`POST .../approve`).
 *
 * İhtar PDF akışı (S-5, BAĞLAYICI karar): ihtarname yalnızca bir icra
 * dosyasına bağlı üretilir — dosya yoksa PDF isteği HİÇ atılmaz, kullanıcı
 * dosya açma akışına (`/admin/legal`) yönlendirilir. 1 dosya varsa direkt
 * indirilir, >1 dosya varsa Popover'dan seçim istenir.
 *
 * Muhasebe FAZ 4 (Tur 2, 2026-09-29, feature/parasut-fatura-faz4-web) —
 * `usePaymentsInvoiceStatusBulk` TÜM tahsilat satırlarının Paraşüt fatura
 * durumunu TEK istekte çeker (satır başına istek YOK), `parasutStatusMap`
 * olarak hem `DebtLedgerTable` (rozet kolonu) hem `ReversePaymentDialog`
 * (fatura-kesilmiş uyarısı) tarafına geçirilir. `studentTcNo` boşsa (TCKN
 * eksik — Paraşüt cari eşlemesi `blocked`/`missing_tax_number` kalır, bkz.
 * `services/parasut/contacts.py`) üstte bir uyarı + Kimlik sekmesine
 * yönlendirme gösterilir. "Adres" alanı bu turda KONTROL EDİLMEDİ — backend
 * öğrenci profilinde henüz bir adres alanı YOK (bkz. `contacts.py` modül
 * docstring'i "adres yok" notu); uydurma bir alan kontrolü eklenmedi.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Receipt, ScrollText, Gavel, Clock, Lock, X, UserRound } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext';
import { EmptyState, LoadingState, StatusBadge } from '@/components/admin';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { fmtMoneyAccounting } from '@/utils/money';
import { useStudentLedger } from '@/api/queries/students';
import { useDownloadStatementPdf, useDownloadNoticePdf, useLegalCasesForNotice } from '@/api/mutations/accountingDocuments';
import { usePendingReversals, useApproveReversal, useRejectReversal, useAccountingBanks } from '@/api/queries/accounting';
import { useMokaConfig } from '@/api/mutations/moka';
import { usePaymentsInvoiceStatusBulk } from '@/api/admin/parasutMapping';
import MokaLinkDialog from '@/components/admin/moka/MokaLinkDialog';
import { ActionButton, InlineAlert } from './noteUiKit';
import { fmtLedgerDate, TERMINAL_DEBT_ROW_STATUSES } from './columnDefs';
import DebtLedgerTable from './DebtLedgerTable';
import CollectionForm from './CollectionForm';
import ReversePaymentDialog, { reversePaymentErrorMessage } from './ReversePaymentDialog';

// İcra dosyası durumu -> StatusBadge varyantı. Etiket AYRICA yeni bir anahtar
// açmadan mevcut `admin.legal.status.*` ailesinden okunur (AdminLegal.jsx'in
// kendi STATUS_COLORS'ıyla AYNI görsel eşleme, farklı bir dosyada tekrar
// tanımlandı — AdminLegal.jsx bu turda dokunulmayan/read-only dosya).
const LEGAL_CASE_STATUS_VARIANT = {
  pending: 'warning',
  notice_sent: 'info',
  legal_started: 'purple',
  payment_order: 'warning',
  seizure: 'danger',
  collected: 'success',
  closed: 'neutral',
};

// `tone: 'info'` — FAZ 1-B (Seçenek C, faiz): "Tahsil Edilen Faiz" kartı
// diğer üç toplam kartıyla AYNI görsel ailede olursa "toplamın bir parçası"
// gibi okunur (yanlış sinyal — faiz kalan_bakiye hesabına dahil DEĞİL).
// Nötr/bilgi tonu (sky) + isteğe bağlı `hint` alt satırı bu ayrımı taşır.
function SummaryCard({ label, value, colorClass, hint, tone = 'default' }) {
  const boxClass = tone === 'info' ? 'border-sky-200 bg-sky-50/60' : 'border-[var(--border-light)] bg-white';
  return (
    <div className={`rounded-xl border-2 ${boxClass} p-3 text-center`}>
      <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-lg font-display font-bold ${colorClass}`}>{value}</p>
      {hint && <p className="text-[9px] leading-snug text-[var(--text-muted)] mt-1">{hint}</p>}
    </div>
  );
}

/**
 * Popover içindeki tek bekleyen ters-kayıt talebi kartı (FAZ 6.8).
 *
 * Maker-checker: `isOwnRequest` true ise Onayla `disabled` + yanında HER ZAMAN
 * görünür (hover tooltip DEĞİL — mobilde hover yok) `Lock` ikonu + açıklama.
 * Reddet herkes için aktif kalır (kendi hatalı talebini geri çekebilmeli).
 */
function PendingReversalItem({ item, isOwnRequest, onApprove, onReject, actioning, t, lang }) {
  const isApproving = actioning === `approve_${item.reversal_id}`;
  const isRejecting = actioning === `reject_${item.reversal_id}`;
  const busy = isApproving || isRejecting;

  return (
    <div
      className="rounded-xl border-2 border-[var(--border-light)] bg-white p-3 space-y-1.5"
      data-testid={`pending-reversals-item-${item.reversal_id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-bold text-sm tabular-nums">{fmtMoneyAccounting(item.payment_amount, lang)}</span>
        <span className="text-[11px] text-[var(--text-muted)]">{fmtLedgerDate(item.payment_created_at, lang)}</span>
      </div>
      <p className="text-xs text-[var(--text-main)] line-clamp-2">{item.reason}</p>
      <p className="text-[11px] text-[var(--text-muted)]">
        {item.created_by_name || '—'} · {fmtLedgerDate(item.created_at, lang)}
      </p>
      <div className="flex items-center gap-2 pt-1">
        <ActionButton
          variant="success"
          onClick={() => onApprove(item.reversal_id)}
          disabled={isOwnRequest || busy}
          loading={isApproving}
          testId={`pending-reversals-approve-btn-${item.reversal_id}`}
          className="flex-1"
        >
          {t('accounting.reverse_payment.approve_button')}
        </ActionButton>
        <ActionButton
          variant="danger"
          icon={X}
          onClick={() => onReject(item.reversal_id)}
          disabled={busy}
          loading={isRejecting}
          testId={`pending-reversals-reject-btn-${item.reversal_id}`}
        >
          {t('accounting.reverse_payment.reject_button')}
        </ActionButton>
      </div>
      {isOwnRequest && (
        <div className="flex items-start gap-1.5 text-[10px] font-bold text-amber-700">
          <Lock size={12} className="flex-shrink-0 mt-0.5" aria-hidden />
          <span>{t('accounting.errors.maker_checker_ihlali')}</span>
        </div>
      )}
    </div>
  );
}

/**
 * "Onay Bekliyor" rozet-buton + Popover (FAZ 6.8, S-x). `count === 0` iken
 * HİÇ render edilmez — bu yüzden `usePendingReversals` popover açık/kapalı
 * durumundan BAĞIMSIZ her zaman çalışır (sayaç popover açılmadan önce de
 * gerekli).
 */
function PendingReversalsBadge({ userId }) {
  const { t, lang } = useLanguage();
  const toast = useToast();
  const { user } = useAuth();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [actioning, setActioning] = useState(null);
  const { data, isLoading, isError, refetch } = usePendingReversals(userId);
  const approveReversal = useApproveReversal(userId);
  const rejectReversal = useRejectReversal(userId);

  const items = data?.items || [];
  const count = data?.total ?? items.length;

  if (!count) return null;

  const handleApprove = async (reversalId) => {
    setActioning(`approve_${reversalId}`);
    try {
      await approveReversal.mutateAsync(reversalId);
      toast.success(t('accounting.reverse_payment.approve_success'));
    } catch (err) {
      toast.error(reversePaymentErrorMessage(err, lang, t));
    } finally {
      setActioning(null);
    }
  };

  const handleReject = async (reversalId) => {
    setActioning(`reject_${reversalId}`);
    try {
      await rejectReversal.mutateAsync({ reversalId });
      toast.success(t('accounting.reverse_payment.reject_success'));
    } catch (err) {
      toast.error(reversePaymentErrorMessage(err, lang, t));
    } finally {
      setActioning(null);
    }
  };

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl border-2 border-amber-400 bg-amber-50 text-amber-700 text-sm font-bold hover:bg-amber-100 transition-colors w-full sm:w-auto"
          data-testid="pending-reversals-badge-btn"
        >
          <Clock size={15} />
          {t('accounting.reverse_payment.pending_list_label').replace('{count}', String(count))}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 max-h-96 overflow-y-auto space-y-2 bg-white"
        data-testid="pending-reversals-popover"
      >
        {isLoading && (
          <div data-testid="pending-reversals-loading">
            <LoadingState variant="bars" count={2} />
          </div>
        )}
        {!isLoading && isError && (
          <InlineAlert variant="danger" testId="pending-reversals-error">
            <p>{t('accounting.error.description')}</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-1.5 font-bold underline underline-offset-2"
            >
              {t('accounting.error.retry')}
            </button>
          </InlineAlert>
        )}
        {!isLoading && !isError && items.length === 0 && (
          <InlineAlert variant="info" testId="pending-reversals-empty">
            {t('accounting.reverse_payment.pending_list_empty')}
          </InlineAlert>
        )}
        {!isLoading && !isError && items.map((item) => (
          <PendingReversalItem
            key={item.reversal_id}
            item={item}
            isOwnRequest={!!user?.user_id && item.created_by === user.user_id}
            onApprove={handleApprove}
            onReject={handleReject}
            actioning={actioning}
            t={t}
            lang={lang}
          />
        ))}
      </PopoverContent>
    </Popover>
  );
}

export default function DebtLedgerSubTab({ userId, enrollments, studentName, studentTcNo, onNavigateToProfile }) {
  const { t, lang } = useLanguage();
  const toast = useToast();
  const navigate = useNavigate();
  const { data: ledger, isLoading, isError, refetch } = useStudentLedger(userId);
  const [collectionOpen, setCollectionOpen] = useState(false);
  const [collectionCourseId, setCollectionCourseId] = useState('');
  const [collectionInstallmentRow, setCollectionInstallmentRow] = useState(null);
  // F1-B fix (fix/tahsilat-taksit-baglama, reviewer Yüksek bulgusu) — taksitsiz
  // (row_type="invoice") bir borç satırından açılan tahsilat artık bu satırın
  // invoice_id'sine (CollectionForm.jsx `invoiceRow` prop'u) bağlanır.
  const [collectionInvoiceRow, setCollectionInvoiceRow] = useState(null);
  const [installmentPickerOpen, setInstallmentPickerOpen] = useState(false);
  const [reverseRow, setReverseRow] = useState(null);
  // Faz 2 (feature/moka-profil-link, 2026-09-28) — satır-bazlı "Ödeme linki
  // gönder" (columnDefs.js `action` kolonu + DebtLedgerTable.jsx mobil kart)
  // MokaLinkDialog'u AdminAccounting.jsx'teki (satır ~3413) AYNI bileşenle
  // açar; `mokaConfig.link_active` kapalıysa aksiyon pasif + açıklama.
  const [mokaLinkRow, setMokaLinkRow] = useState(null);
  const { data: mokaConfig } = useMokaConfig();
  const { data: banks } = useAccountingBanks();

  // Muhasebe FAZ 4 (Tur 2) — Paraşüt fatura durumu, TÜM tahsilat satırları
  // için TEK istekte (satır başına istek YOK). `ledger` henüz yüklenmediyse
  // `paymentIds` boş dizi — hook `enabled` guard'ı sayesinde istek atmaz
  // (bkz. usePaymentsInvoiceStatusBulk docstring'i, hook'lar KOŞULSUZ
  // çağrılmalı — bu yüzden aşağıdaki erken `return`'lerden ÖNCE burada).
  const paymentIdsForParasutStatus = (ledger?.collection_rows || []).map((r) => r.id).filter(Boolean);
  const { data: parasutInvoiceStatusData } = usePaymentsInvoiceStatusBulk(paymentIdsForParasutStatus);
  const parasutStatusMap = parasutInvoiceStatusData?.results || {};

  const debtRowsForPicker = ledger?.debt_rows || [];
  // F1 fix (fix/tahsilat-taksit-baglama) — tahsil edilebilir (açık, terminal
  // olmayan) taksit satırları. Genel "Tahsilat" butonu bunlardan biri varsa
  // önce seçim istiyor — bkz. openCollection(null) aşağıda.
  const openInstallmentRows = debtRowsForPicker.filter(
    (r) => r.row_type === 'installment' && (r.remaining || 0) > 0 && !TERMINAL_DEBT_ROW_STATUSES.includes(r.status)
  );
  // F1-B fix — tahsil edilebilir taksitsiz açık fatura satırları (row_type=
  // "invoice"). Genel "Tahsilat" butonu artık bunları da seçiciye ekler —
  // birden fazla varsa backend zaten `fatura_secimi_gerekli` ile 400 döner
  // (routes/admin.py::admin_add_student_payment), bu picker o belirsizliği
  // kullanıcıya sormadan önce ÇÖZER.
  const openInvoiceRows = debtRowsForPicker.filter(
    (r) => r.row_type === 'invoice' && (r.remaining || 0) > 0 && !TERMINAL_DEBT_ROW_STATUSES.includes(r.status)
  );
  const openDebtRowsForPicker = [...openInstallmentRows, ...openInvoiceRows];

  const openCollection = (row) => {
    // B2 fix — iptal edilmiş/ödenmiş bir borç satırı için tahsilat formu hiç
    // açılmaz; kullanıcı NEDEN engellendiğini toast ile görür. Artık
    // SAVUNMA katmanı: kriter 3'ün asıl çözümü butonun kendisini kapatmak
    // (columnDefs.js `action` kolonu + DebtLedgerTable.jsx MobileLedgerCards,
    // ikisi de aynı TERMINAL_DEBT_ROW_STATUSES'i kullanır) — terminal satırda
    // buton artık zaten `onCollect` çağırmıyor, bu guard normalde tetiklenmez
    // ama örn. gelecekte başka bir çağrı yolu açılırsa yine de kapıyı tutar.
    if (row && TERMINAL_DEBT_ROW_STATUSES.includes(row.status)) {
      toast.warning(t('accounting.debt_ledger.collect_blocked_terminal'));
      return;
    }

    // F1 fix (fix/tahsilat-taksit-baglama) — satır bazlı tahsilat: bir taksit
    // satırında tıklanmışsa form "taksit modu"na gider (CollectionForm.jsx,
    // POST /admin/installments/{id}/collect — doğru faturaya bağlanır, kök
    // çözüm).
    if (row && row.row_type === 'installment') {
      setCollectionInstallmentRow(row);
      setCollectionInvoiceRow(null);
      setCollectionCourseId('');
      setCollectionOpen(true);
      return;
    }

    // F1-B fix — taksitsiz bir fatura satırında (row_type="invoice")
    // tıklanmışsa form bu invoice_id'ye bağlı açılır (tutar kalanla ön-dolu,
    // tavan kalan — bkz. CollectionForm.jsx `invoiceRow` prop'u). Önceden bu
    // dal genel/belirsiz add-payment akışına düşüyordu (backend en eski açık
    // faturayı seçiyordu — kök bug, reviewer Yüksek bulgusu).
    if (row && row.row_type === 'invoice') {
      setCollectionInstallmentRow(null);
      setCollectionInvoiceRow(row);
      setCollectionCourseId(row.course_id || '');
      setCollectionOpen(true);
      return;
    }

    // Satır dışındaki genel "Tahsilat" butonu (row === null) — öğrencinin
    // ödenmemiş taksit VEYA taksitsiz açık fatura satırı varsa artık
    // SESSİZCE en eski açık faturaya bağlanmıyor: önce hangi borç satırına
    // tahsilat yapılacağı sorulur (picker, F1-B fix'te taksit + fatura
    // birlikte listelenir).
    if (!row && openDebtRowsForPicker.length > 0) {
      setInstallmentPickerOpen(true);
      return;
    }

    setCollectionInstallmentRow(null);
    setCollectionInvoiceRow(null);
    setCollectionCourseId(row?.course_id || '');
    setCollectionOpen(true);
  };

  // F1-B fix — picker artık taksit VE fatura satırlarını birlikte listeler;
  // seçilen satırın row_type'ına göre doğru moda yönlendirir.
  const pickDebtRowForCollection = (row) => {
    setInstallmentPickerOpen(false);
    if (row.row_type === 'installment') {
      setCollectionInstallmentRow(row);
      setCollectionInvoiceRow(null);
      setCollectionCourseId('');
    } else {
      setCollectionInstallmentRow(null);
      setCollectionInvoiceRow(row);
      setCollectionCourseId(row.course_id || '');
    }
    setCollectionOpen(true);
  };

  // ── Döküm PDF İndir ────────────────────────────────────────────────────────
  const [statementLoading, setStatementLoading] = useState(false);
  const downloadStatement = useDownloadStatementPdf();
  const handleDownloadStatement = async () => {
    if (statementLoading) return;
    setStatementLoading(true);
    try {
      await downloadStatement.mutateAsync(userId);
      toast.success(t('accounting.docs.statement_pdf.success'));
    } catch {
      toast.error(t('accounting.docs.statement_pdf.error'));
    } finally {
      setStatementLoading(false);
    }
  };

  // ── İhtar PDF İndir (S-5: yalnızca icra dosyası üzerinden) ──────────────────
  const [noticeLoading, setNoticeLoading] = useState(false);
  const [noCaseAlert, setNoCaseAlert] = useState(false);
  const [casePickerOpen, setCasePickerOpen] = useState(false);
  const [legalCases, setLegalCases] = useState([]);
  const fetchLegalCases = useLegalCasesForNotice();
  const downloadNotice = useDownloadNoticePdf();

  const doDownloadNotice = async (caseId) => {
    setNoticeLoading(true);
    try {
      await downloadNotice.mutateAsync(caseId);
      toast.success(t('accounting.docs.notice_pdf.success'));
    } catch {
      toast.error(t('accounting.docs.notice_pdf.error'));
    } finally {
      setNoticeLoading(false);
    }
  };

  const handleNoticeClick = async () => {
    if (noticeLoading) return;
    setNoCaseAlert(false);
    setNoticeLoading(true);
    try {
      const cases = await fetchLegalCases.mutateAsync(userId);
      if (!cases || cases.length === 0) {
        // S-5: dosya yoksa PDF isteği hiç atılmaz.
        setNoCaseAlert(true);
        setNoticeLoading(false);
        return;
      }
      if (cases.length === 1) {
        await doDownloadNotice(cases[0].case_id);
        return;
      }
      setLegalCases(cases);
      setCasePickerOpen(true);
      setNoticeLoading(false);
    } catch {
      toast.error(t('accounting.docs.notice_pdf.error'));
      setNoticeLoading(false);
    }
  };

  const handlePickCase = async (caseId) => {
    setCasePickerOpen(false);
    await doDownloadNotice(caseId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <LoadingState />
      </div>
    );
  }

  if (isError) {
    return (
      <EmptyState
        icon={<AlertCircle size={40} strokeWidth={1.5} className="text-rose-500" />}
        title={t('accounting.error.title')}
        description={t('accounting.error.description')}
        action={{ label: t('accounting.error.retry'), onClick: () => refetch() }}
      />
    );
  }

  const summary = ledger?.summary || {};
  const debtRows = ledger?.debt_rows || [];
  const collectionRows = ledger?.collection_rows || [];

  // FAZ 1-B (Seçenek B, iade): `kalan_bakiye` artık NEGATİF olabilir — IAD
  // faturası borcu aşarsa öğrenci alacaklı durumuna geçer. Üç durum ayrı
  // renk + ayrı etiket taşır (eski `> 0 ? amber : emerald` ikilisi negatifte
  // "sorun yok" diyordu — yanlış sinyal). AccountingSummarySubTab.jsx AYNI
  // kararı `invoice_outstanding` için tekrarlar, iki ekran tutarlı kalsın diye.
  const kalanBakiye = summary.kalan_bakiye || 0;
  let balanceLabel = t('accounting.debt_ledger.balance');
  let balanceColor = 'text-emerald-600';
  let balanceValue = kalanBakiye;
  if (kalanBakiye > 0) {
    balanceColor = 'text-amber-600';
  } else if (kalanBakiye < 0) {
    balanceColor = 'text-rose-600';
    balanceLabel = t('accounting.debt_ledger.balance_creditor');
    balanceValue = Math.abs(kalanBakiye);
  }
  const toplamFaiz = summary.toplam_faiz || 0;

  return (
    <div className="space-y-4">
      {/* Muhasebe FAZ 4 (Tur 2) — TCKN eksikse Paraşüt cari eşlemesi
          kurulamaz (blocked/missing_tax_number, bkz. services/parasut/
          contacts.py). Proaktif üst uyarı — admin satır satır keşfetmeden
          önce sorunu görsün. */}
      {!studentTcNo && (
        <InlineAlert variant="warning" testId="debt-ledger-missing-tc-warning">
          <p>{t('accounting.parasut_status.missing_tc_top_warning')}</p>
          {onNavigateToProfile && (
            <button
              type="button"
              onClick={onNavigateToProfile}
              className="mt-1.5 inline-flex items-center gap-1 font-bold underline underline-offset-2"
              data-testid="debt-ledger-missing-tc-goto-profile"
            >
              <UserRound size={13} />
              {t('accounting.parasut_status.missing_tc_goto_profile')}
            </button>
          )}
        </InlineAlert>
      )}

      <div className="grid grid-cols-3 gap-3">
        <SummaryCard
          label={t('accounting.debt_ledger.total_debt')}
          value={fmtMoneyAccounting(summary.toplam_borc, lang)}
          colorClass="text-rose-600"
        />
        <SummaryCard
          label={t('accounting.debt_ledger.total_collected')}
          value={fmtMoneyAccounting(summary.toplam_tahsilat, lang)}
          colorClass="text-emerald-600"
        />
        <SummaryCard label={balanceLabel} value={fmtMoneyAccounting(balanceValue, lang)} colorClass={balanceColor} />
      </div>

      {/* FAZ 1-B (Seçenek C, faiz): kapalı motorda çoğu öğrencide 0 —
          gürültü olmasın diye 0 iken hiç render edilmez. Ayrı satırda,
          "info" tonuyla — üç toplam kartının bir parçası GİBİ görünmez. */}
      {toplamFaiz > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="debt-ledger-interest-row">
          <SummaryCard
            label={t('accounting.debt_ledger.total_interest')}
            value={fmtMoneyAccounting(toplamFaiz, lang)}
            colorClass="text-sky-600"
            hint={t('accounting.debt_ledger.interest_hint')}
            tone="info"
          />
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        <PendingReversalsBadge userId={userId} />

        <ActionButton
          variant="neutral"
          onClick={handleDownloadStatement}
          loading={statementLoading}
          icon={ScrollText}
          testId="download-statement-pdf-btn"
          className="w-full sm:w-auto"
        >
          {statementLoading ? t('accounting.docs.statement_pdf.loading') : t('accounting.docs.statement_pdf.label')}
        </ActionButton>

        <Popover open={casePickerOpen} onOpenChange={setCasePickerOpen}>
          <PopoverAnchor asChild>
            <ActionButton
              variant="neutral"
              onClick={handleNoticeClick}
              loading={noticeLoading}
              icon={Gavel}
              testId="download-notice-pdf-btn"
              className="w-full sm:w-auto"
            >
              {noticeLoading ? t('accounting.docs.notice_pdf.loading') : t('accounting.docs.notice_pdf.label')}
            </ActionButton>
          </PopoverAnchor>
          <PopoverContent align="end" className="w-72 space-y-2 bg-white">
            <p className="text-xs font-bold text-[var(--text-main)]">{t('accounting.docs.notice_pdf.picker_title')}</p>
            <p className="text-[11px] text-[var(--text-muted)]">{t('accounting.docs.notice_pdf.picker_hint')}</p>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {legalCases.map((lc) => (
                <button
                  key={lc.case_id}
                  type="button"
                  onClick={() => handlePickCase(lc.case_id)}
                  className="w-full flex items-center justify-between gap-2 p-2 rounded-lg border-2 border-[var(--border-light)] text-left text-xs hover:bg-gray-50 transition-colors"
                  data-testid={`notice-pdf-case-option-${lc.case_id}`}
                >
                  <span className="flex flex-col gap-0.5">
                    <span className="font-bold">{lc.case_no || '—'}</span>
                    <span className="text-[var(--text-muted)]">
                      {fmtMoneyAccounting(lc.debt_amount, lang)} · {fmtLedgerDate(lc.opened_at || lc.created_at, lang)}
                    </span>
                  </span>
                  <StatusBadge
                    variant={LEGAL_CASE_STATUS_VARIANT[lc.status] || 'neutral'}
                    label={t(`admin.legal.status.${lc.status}`)}
                  />
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <Popover open={installmentPickerOpen} onOpenChange={setInstallmentPickerOpen}>
          <PopoverAnchor asChild>
            <button
              type="button"
              onClick={() => openCollection(null)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 border-b-4 border-b-emerald-700 w-full sm:w-auto"
              data-testid="open-collection-form-btn"
            >
              <Receipt size={15} />
              {t('accounting.actions.collect')}
            </button>
          </PopoverAnchor>
          <PopoverContent align="end" className="w-80 space-y-2 bg-white" data-testid="installment-picker-popover">
            {/* F1-B fix — picker artık taksit + taksitsiz fatura satırlarını
                birlikte listeler; tek tip kalan yoksa (yalnızca taksit VEYA
                yalnızca fatura) başlık/ipucu eski F1 metniyle BİREBİR aynı
                kalır — davranış/kopya gereksiz yere değişmesin. */}
            <p className="text-xs font-bold text-[var(--text-main)]">
              {openInvoiceRows.length > 0
                ? t('accounting.debt_ledger.pick_debt_row_title')
                : t('accounting.debt_ledger.pick_installment_title')}
            </p>
            <p className="text-[11px] text-[var(--text-muted)]">
              {openInvoiceRows.length > 0
                ? t('accounting.debt_ledger.pick_debt_row_hint')
                : t('accounting.debt_ledger.pick_installment_hint')}
            </p>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {openDebtRowsForPicker.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => pickDebtRowForCollection(row)}
                  className="w-full flex items-center justify-between gap-2 p-2 rounded-lg border-2 border-[var(--border-light)] text-left text-xs hover:bg-gray-50 transition-colors"
                  data-testid={`installment-picker-option-${row.id}`}
                >
                  <span className="flex flex-col gap-0.5 min-w-0">
                    <span className="font-bold truncate">{row.course_title || row.course_id || '—'}</span>
                    <span className="text-[var(--text-muted)]">
                      {row.row_type === 'installment'
                        ? t('accounting.debt_ledger.pick_installment_label')
                            .replace('{no}', row.installment_no ?? '—')
                            .replace('{total}', row.total_installments ?? '—')
                        : t('accounting.debt_ledger.pick_invoice_label')}
                      {' · '}
                      {fmtLedgerDate(row.date, lang)}
                    </span>
                  </span>
                  <span className="font-bold tabular-nums flex-shrink-0">{fmtMoneyAccounting(row.remaining, lang)}</span>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {noCaseAlert && (
        <InlineAlert
          variant="warning"
          title={t('accounting.docs.notice_pdf.no_case_title')}
          testId="notice-pdf-no-case-alert"
        >
          <p>{t('accounting.docs.notice_pdf.no_case_desc')}</p>
          <button
            type="button"
            onClick={() => navigate('/admin/legal')}
            className="mt-1.5 font-bold underline underline-offset-2"
            data-testid="notice-pdf-no-case-link"
          >
            {t('accounting.docs.notice_pdf.no_case_link')}
          </button>
        </InlineAlert>
      )}

      <DebtLedgerTable
        debtRows={debtRows}
        collectionRows={collectionRows}
        loading={false}
        onCollect={openCollection}
        onReverse={setReverseRow}
        onMokaLink={setMokaLinkRow}
        mokaConfig={mokaConfig}
        parasutStatusMap={parasutStatusMap}
      />

      <CollectionForm
        open={collectionOpen}
        onOpenChange={(v) => {
          setCollectionOpen(v);
          // F1/F1-B fix — kapanışta taksit/fatura modu kalıntısı bir sonraki
          // genel açılışına sızmasın (aksi halde bir sonraki
          // openCollection(null) çağrısı yanlışlıkla önceki satıra bağlı
          // kalabilir).
          if (!v) {
            setCollectionInstallmentRow(null);
            setCollectionInvoiceRow(null);
          }
        }}
        userId={userId}
        enrollments={enrollments}
        initialCourseId={collectionCourseId}
        installmentRow={collectionInstallmentRow}
        invoiceRow={collectionInvoiceRow}
      />

      <ReversePaymentDialog
        open={!!reverseRow}
        onOpenChange={(v) => { if (!v) setReverseRow(null); }}
        userId={userId}
        row={reverseRow}
        parasutStatus={reverseRow ? parasutStatusMap[reverseRow.id] : null}
      />

      {/* Faz 2 (feature/moka-profil-link, 2026-09-28) — `mokaLinkRow` bir
          debt_rows["installment"] satırı; MokaLinkDialog'un beklediği
          `installment` şekline haritalanır (`amount_with_interest` ->
          `row.remaining`, açık taksitte zaten faiz dahil kalan tutarla AYNI,
          bkz. routes/payments.py::admin_student_accounting_ledger docstring'i). */}
      {mokaLinkRow && (
        <MokaLinkDialog
          installment={{
            installment_id: mokaLinkRow.id,
            amount: mokaLinkRow.amount,
            amount_with_interest: mokaLinkRow.remaining,
            installment_no: mokaLinkRow.installment_no,
            total_installments: mokaLinkRow.total_installments,
            user_name: studentName,
          }}
          banks={banks || []}
          onClose={() => setMokaLinkRow(null)}
          t={t}
        />
      )}
    </div>
  );
}
